import React, { useMemo } from 'react';

import noop from 'lodash/noop';

import { classnames } from '@lumapps/classnames';
import { useOption } from '@lumapps/lumx/hooks/useOption';
import {
    mdiCheckboxBlankOutline,
    mdiCheckboxMarked,
    mdiCheckCircle,
    mdiCircleOutline,
    mdiRadioboxBlank,
    mdiRadioboxMarked,
} from '@lumapps/lumx/icons';
import { Icon, IconProps, Size, Text } from '@lumapps/lumx/react';
import { MovingFocusProvider } from '@lumapps/moving-focus';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import './index.scss';

export const CLASSNAME = 'picker-list';

type PickerListProps<I> = {
    'aria-labelledby': string;
    items: I[];
    disabledItems?: I[];
    alreadyUsedItems?: I[];
    itemRenderer: React.FC<I>;
    itemKeySelector(item: I): string | number;
    itemIdSelector?(item: I): string;
    afterList?: React.ReactNode;
    className?: string;
    /** changes the icon from rounded check to checked radio button is single selection */
    isRadio?: boolean;
} & (
    | {
          isMultiple?: false;
          selectedItem: I;
          setSelectedItem(item?: I): void;
          selectedItems?: never;
          setSelectedItems?: never;
      }
    | {
          isMultiple: true;
          selectedItem?: never;
          setSelectedItem?: never;
          selectedItems: I[];
          setSelectedItems(item?: I[]): void;
      }
);

/**
 * List-like component implementing the "listbox" role and the roving tab index pattern.
 *
 * @family Lists
 */
export const PickerList = <I,>(props: PickerListProps<I>) => {
    const {
        isMultiple = false,
        items,
        setSelectedItems = noop, // to satisfy typescript when isMultiple is false
        selectedItems,
        setSelectedItem = noop, // to satisfy typescript when isMultiple is true
        selectedItem,
        disabledItems = [],
        alreadyUsedItems = [],
        itemKeySelector,
        itemIdSelector,
        itemRenderer: ItemRenderer,
        isRadio = false,
        afterList,
        className,
        ...forwardedProps
    } = props;

    const isInList = (list: I[], item: I) => {
        return list.findIndex((i) => itemKeySelector(i) === itemKeySelector(item)) > -1;
    };

    const onSelect = (item: I) => () => {
        if (isMultiple) {
            const nextSelectedItems = isInList(selectedItems as I[], item)
                ? (selectedItems as I[]).filter((i) => i !== item)
                : [...(selectedItems as I[]), item];

            setSelectedItems(nextSelectedItems);
        } else {
            setSelectedItem(item);
        }
    };

    return (
        <MovingFocusProvider options={{ direction: 'both', loopAround: true }}>
            <ul
                className={classnames(CLASSNAME, className)}
                role="listbox"
                aria-multiselectable={isMultiple}
                {...forwardedProps}
            >
                {items?.map((item) => (
                    // eslint-disable-next-line no-use-before-define
                    <PickerListItem
                        id={itemIdSelector && itemIdSelector(item)}
                        isSelected={isMultiple ? isInList(selectedItems as I[], item) : item === selectedItem}
                        isDisabled={isInList(disabledItems, item)}
                        isAlreadyUsed={isInList(alreadyUsedItems, item)}
                        selectItem={onSelect(item)}
                        isInMultipleSelect={isMultiple}
                        isRadio={isRadio}
                        key={itemKeySelector(item)}
                    >
                        <ItemRenderer {...(item as I & JSX.IntrinsicAttributes)} />
                    </PickerListItem>
                ))}
                {afterList}
            </ul>
        </MovingFocusProvider>
    );
};

PickerList.displayName = 'PickerList';

interface PickerListItemProps {
    id?: string;
    isSelected: boolean;
    isInMultipleSelect: boolean;
    isDisabled: boolean;
    isAlreadyUsed: boolean;
    isRadio: boolean;
    selectItem(): void;
}

/**
 * ListItem-like component implementing the "option" role and the rolling tab index pattern.
 */
export const PickerListItem: React.FC<PickerListItemProps> = ({
    isSelected,
    selectItem,
    children,
    isInMultipleSelect,
    isDisabled,
    isAlreadyUsed,
    id,
    isRadio,
}) => {
    const { translateKey } = useTranslate();

    const icon = useMemo(() => {
        if (isInMultipleSelect) {
            return isSelected ? mdiCheckboxMarked : mdiCheckboxBlankOutline;
        }
        if (isRadio) {
            return isSelected ? mdiRadioboxMarked : mdiRadioboxBlank;
        }
        return isSelected ? mdiCheckCircle : mdiCircleOutline;
    }, [isInMultipleSelect, isRadio, isSelected]);

    const selectionIconProps: IconProps = isSelected ? { icon } : { icon, color: 'dark', colorVariant: 'N' };

    const ref = React.useRef<HTMLDivElement>(null);

    // ARIA option & roving tab index
    const { tabIndex, onClick, onKeyDown } = useOption(
        ref,
        selectItem,
        isDisabled || isAlreadyUsed,
        isInMultipleSelect,
    );

    return (
        // As the ul has role of listbox, the li must have a presentation role and the actual items must have the option role
        <li className={`${CLASSNAME}-item`} role="presentation">
            <div
                ref={ref}
                className={classnames(
                    `${CLASSNAME}-item__link`,
                    isSelected && `${CLASSNAME}-item__link--is-selected`,
                    !isDisabled && `${CLASSNAME}-item__link--is-clickable`,
                    isDisabled && !isAlreadyUsed && `${CLASSNAME}-item__link--is-disabled`,
                )}
                // ARIA option role.
                role="option"
                // ARIA selected.
                aria-selected={isSelected}
                // ARIA checked.
                aria-disabled={isDisabled || undefined}
                // ARIA roving tab index state.
                tabIndex={tabIndex}
                // ARIA roving tab index keyboard navigation behavior.
                onKeyDown={onKeyDown}
                // ARIA role option: select on click + focus on click.
                onClick={isDisabled || isAlreadyUsed ? undefined : onClick}
                aria-labelledby={id && `option-${id}-title`}
                aria-describedby={id && `option-${id}-description`}
            >
                <div className={`${CLASSNAME}-item__content`}>{children}</div>
                <div className={`${CLASSNAME}-item__after`}>
                    {isAlreadyUsed ? (
                        <Text as="span" typography="subtitle1">
                            {translateKey(GLOBAL.ALREADY_ADDED)}
                        </Text>
                    ) : (
                        <Icon size={Size.s} {...selectionIconProps} />
                    )}
                </div>
            </div>
        </li>
    );
};
PickerListItem.displayName = 'PickerListItem';
